import type { SoftphoneProps, SoftphoneLocalConfigProps } from './types';
import { FloatingWrapper } from 'Components/shared/FloatingWrapper';
import { TopBarDialpad } from 'Components/TopBarDialpad';
import { SOFTPHONE_DIMENSIONS_POSITION } from 'Constants/localstorage';
import {
  STORE_CONVERSATION,
  STORE_NOTIFICATION,
  STORE_PHONE_CALL,
  STORE_PREFERENCE,
  STORE_UI,
} from 'Constants/stores';
import CallSidebar from 'Containers/CallSidebar';
import { usePreferences } from 'Hooks/preferences/usePreferences'; // Import the hook
import localforage from 'localforage';
import { debounce, max } from 'lodash';
import { inject, observer } from 'mobx-react';
import { useSoftphoneStore } from 'Modules/softphone/store';
import React, { useState, useCallback, useEffect } from 'react';
import { DraggableEvent } from 'react-draggable';
import { DraggableData, Position, ResizableDelta } from 'react-rnd';
import SoftphoneAudios from '../SoftphoneAudios';

const Softphone = (props: SoftphoneProps) => {
  const [statePosition, setStatePosition] = useState({
    x: window.innerWidth - 310 - 70,
    y: 80,
  });
  const [stateDimensions, setStateDimensions] = useState({
    width: 310,
    height: 550,
  });
  const {
    conversation: { listOfIncomingVideoCalls },
    phoneCall: {
      ActivePhoneCall,
      incomingPhoneCalls,
      AnyPhoneConnectionActive,
      isCallTimeout,
      isAttemptingToReconnectSip,
      isWebrtcAttemptDone,
    },
  } = props;

  const { isDialpadActive, setDialpadActive } = useSoftphoneStore((state) => ({
    isDialpadActive: state.isDialpadActive,
    setDialpadActive: state.setDialpadActive,
  }));

  const {
    preferences: { floatingSoftphone: isSoftphoneFixed },
  } = usePreferences();

  useEffect(() => {
    void (async () => {
      const res = await localforage.getItem<SoftphoneLocalConfigProps>(
        SOFTPHONE_DIMENSIONS_POSITION
      );
      if (res?.dimensions && res?.position) {
        const wWidth = window.innerWidth;
        const wHeight = window.innerWidth;
        const {
          dimensions: { width, height },
          position: { x, y },
        } = res;

        const newWidth =
          width && res.dimensions.width > wWidth
            ? stateDimensions.width
            : width;
        const newHeight = height > wHeight ? stateDimensions.height : height;
        let newPositionX = statePosition.x;
        let newPositionY = statePosition.y;
        newPositionX = max([x + newWidth > wWidth ? statePosition.x : x, 0]);
        newPositionY = max([
          y + newHeight > wHeight ? statePosition.y : y,
          -70,
        ]);
        if (Number.isFinite(newPositionX) && Number.isFinite(newPositionY)) {
          setStatePosition({
            x: newPositionX,
            y: newPositionY,
          });
        }
        if (Number.isFinite(newWidth) && Number.isFinite(newHeight)) {
          setStateDimensions({ height: newHeight, width: newWidth });
        }
        await localforage.setItem(SOFTPHONE_DIMENSIONS_POSITION, {
          stateDimensions,
          position: {
            x: newPositionX,
            y: newPositionY,
          },
        });
      } else {
        const newHeight = window.innerHeight > 550 ? 550 : window.innerHeight;
        const newPositionY = window.innerHeight > 630 ? 80 : -70;
        setStatePosition((prev) => ({
          x: prev.x,
          y: newPositionY,
        }));
        setStateDimensions((prev) => ({
          height: newHeight,
          width: prev.width,
        }));
        await localforage.setItem(SOFTPHONE_DIMENSIONS_POSITION, {
          stateDimensions,
          statePosition,
        });
      }
    })();

    window.addEventListener('resize', debounce(handleWindowResize, 500));

    return () => {
      window.removeEventListener('resize', debounce(handleWindowResize, 500));
    };
  }, []);

  const handleWindowResize = React.useCallback(async () => {
    const { innerWidth, innerHeight } = window;

    const res = await localforage.getItem<SoftphoneLocalConfigProps>(
      SOFTPHONE_DIMENSIONS_POSITION
    );

    const position = res?.position || statePosition;
    const dimensions = res?.dimensions || stateDimensions;

    let newPositionX = position.x;
    let newPositionY = position.y;

    if (position.x + dimensions.width > innerWidth) {
      newPositionX = innerWidth - dimensions.width;
    }
    if (position.y + dimensions.height > innerHeight) {
      newPositionY = innerHeight - dimensions.height;
    }
    newPositionX = max([newPositionX, 0]);
    newPositionY = max([newPositionY, -70]);
    const bottomEdge = newPositionY + dimensions.height + 70;
    if (bottomEdge > innerHeight) {
      const diff = bottomEdge - innerHeight;
      dimensions.height -= diff;
      setStateDimensions(dimensions);
    }
    setStatePosition({
      x: newPositionX,
      y: newPositionY,
    });
    await localforage.setItem(SOFTPHONE_DIMENSIONS_POSITION, {
      dimensions,
      position: {
        x: newPositionX,
        y: newPositionY,
      },
    });
  }, [stateDimensions, statePosition]);

  const onDragStop = (e: DraggableEvent, data: DraggableData) => {
    const newPosition = {
      x: data.x,
      y: data.y,
    };

    setStatePosition(newPosition);
    localforage.setItem(SOFTPHONE_DIMENSIONS_POSITION, {
      stateDimensions,
      position: newPosition,
    });
  };

  const onResizeStop = (
    e: MouseEvent | TouchEvent,
    dir: string,
    elementRef: HTMLElement,
    delta: ResizableDelta,
    position: Position
  ) => {
    setStateDimensions((prev) => {
      const newDimensions = {
        width: prev.width + delta.width,
        height: prev.height + delta.height,
      };
      localforage.setItem(SOFTPHONE_DIMENSIONS_POSITION, {
        dimensions: newDimensions,
        position,
      });
      return newDimensions;
    });
  };

  const SoftphoneChildren = useCallback(() => {
    if (
      isDialpadActive &&
      !(
        ActivePhoneCall ||
        incomingPhoneCalls.length ||
        isAttemptingToReconnectSip ||
        isWebrtcAttemptDone
      )
    ) {
      return <TopBarDialpad isFixed={isSoftphoneFixed} hidden={props.hidden} />;
    }

    return <CallSidebar isFixed={isSoftphoneFixed} hidden={props.hidden} />;
  }, [
    isDialpadActive,
    ActivePhoneCall,
    incomingPhoneCalls.length,
    isAttemptingToReconnectSip,
    isWebrtcAttemptDone,
    isSoftphoneFixed,
    props.hidden,
  ]);

  const isActiveCall =
    AnyPhoneConnectionActive ||
    isCallTimeout ||
    listOfIncomingVideoCalls.size > 0 ||
    isAttemptingToReconnectSip ||
    isWebrtcAttemptDone;

  const handleOnCloseFloatingWrapper = () => {
    setDialpadActive(false);
  };

  return (
    <>
      {isSoftphoneFixed ? (
        <SoftphoneChildren />
      ) : (
        <FloatingWrapper
          data-testid="softphone-floatingWrapper"
          open={isActiveCall || isDialpadActive}
          isActiveCall={isActiveCall}
          default={{
            ...statePosition,
            ...stateDimensions,
          }}
          position={statePosition}
          size={stateDimensions}
          onDragStop={onDragStop}
          onResizeStop={onResizeStop}
          onClose={handleOnCloseFloatingWrapper}
        >
          <SoftphoneChildren />
        </FloatingWrapper>
      )}
      <SoftphoneAudios />
    </>
  );
};

export default inject(
  STORE_UI,
  STORE_PHONE_CALL,
  STORE_NOTIFICATION,
  STORE_CONVERSATION,
  STORE_PREFERENCE
)(observer(Softphone));
